import { useEffect, useState } from "react";
import * as Select from "@radix-ui/react-select";
import { CheckIcon, ChevronDownIcon } from "@radix-ui/react-icons";
import Auth from "../Auth";
import { AdminRolesHelp } from "./AdminRolesHelp";
import { useAdminAccess } from "../hooks/useAdminAccess";
import { useAdminAccessStore } from "../stores/adminAccessStore";
import { SudoButton } from "../sudokit/components/SudoButton";
import { useConfirmation } from "../sudokit/components/ConfirmationDialog/ConfirmationProvider";

type Assignment = {
  user_id: string;
  email?: string | null;
  role: "support" | "admin";
  created_at: string;
  updated_at: string;
};
type AdminRole = Assignment["role"];

const ROLE_OPTIONS = [
  { value: "support", label: "Support", description: "Customer support tools" },
  { value: "admin", label: "Admin", description: "Full admin access and role management" },
] as const;

function ChangeRoleMenu({ role, disabled, onChange }: { role: AdminRole; disabled: boolean; onChange: (role: AdminRole) => void }) {
  const [open, setOpen] = useState(false);
  const chooseRole = (value: string) => {
    if (value !== "support" && value !== "admin") return;
    if (value === role) return;
    setOpen(false);
    onChange(value);
  };

  // Keep the selection tied to the saved assignment. Choosing an option only
  // opens confirmation; cancelling must never display an unsaved role.
  return (
    <Select.Root
      value={role}
      disabled={disabled}
      open={open}
      onOpenChange={setOpen}
      onValueChange={chooseRole}
    >
      <Select.Trigger asChild>
        <SudoButton
          size="small"
          variant="secondaryGrey"
          aria-label="Change Role"
        >
          Change Role
          <ChevronDownIcon aria-hidden="true" />
        </SudoButton>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          position="popper"
          align="end"
          sideOffset={6}
          className="z-[2147483647] min-w-[260px] max-w-[calc(100vw-24px)] rounded-xl border border-solid border-app-divider bg-app-menu-color p-1.5 shadow-card"
        >
          <Select.Viewport>
            {ROLE_OPTIONS.map((option) => (
              <Select.Item
                key={option.value}
                value={option.value}
                disabled={option.value === role}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  // Cancel the native key action before mounting confirmation:
                  // otherwise Enter can click its newly focused Cancel button.
                  event.preventDefault();
                  chooseRole(option.value);
                }}
                className="relative cursor-pointer rounded-lg py-2.5 pl-3 pr-9 text-left text-app-grey-0 outline-none data-[highlighted]:bg-app-menu-color-hover data-[disabled]:cursor-default"
              >
                <Select.ItemText>
                  <span className="text-sm font-medium">{option.label}</span>
                </Select.ItemText>
                <span className="mt-0.5 block text-xs text-app-grey-1">{option.description}</span>
                <Select.ItemIndicator className="absolute right-3 top-3 text-app-primary-cta-high-contrast">
                  <CheckIcon aria-label="Current role" />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}

/** Role mutations go through Express, where last-Admin protection and audit are atomic. */
export default function AdminRoles() {
  const { can } = useAdminAccess();
  const confirm = useConfirmation("admin-roles");
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [identifier, setIdentifier] = useState("");
  const [role, setRole] = useState<AdminRole>("support");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const allowed = can("admin.manageRoles");
  // Sort a copy so refreshed assignments and role changes keep the same order.
  // Email is the visible account label; fall back to UID when it is unavailable.
  const sortedAssignments = [...assignments].sort((a, b) => {
    if (a.role !== b.role) return a.role === "admin" ? -1 : 1;
    return (a.email || a.user_id).localeCompare(b.email || b.user_id, "en", { sensitivity: "base" }) || a.user_id.localeCompare(b.user_id);
  });

  const refresh = async () => {
    const result = await Auth.fetchWithIdToken("/api/admin/roles/list");
    setAssignments(result.assignments || []);
  };
  useEffect(() => {
    if (allowed) {
      refresh()
        .catch(() => setError("Unable to load admin roles."))
        .finally(() => setLoading(false));
    }
  }, [allowed]);

  const update = async (targetUid: string, nextRole: AdminRole | null, email?: string | null) => {
    await confirm(
      {
        title: nextRole ? "Assign admin role" : "Remove admin access",
        message: `${email ? `${email} (${targetUid})` : targetUid}: ${nextRole ? `set role to ${nextRole}` : "remove all admin permissions"}. Existing sessions will use the new permissions on their next request.`,
        actionLabel: nextRole ? "Save role" : "Remove access",
        destructive: !nextRole,
      },
      async () => {
        setBusy(true);
        setError("");
        try {
          await Auth.fetchWithIdToken("/api/admin/roles/set", { uid: targetUid, role: nextRole });
          const access = useAdminAccessStore.getState();
          // Removing our own Admin role succeeds, but the next list request is
          // correctly denied. Refresh authority first so that success doesn't
          // leave a stale management screen showing an error.
          const latest = await access.load(access.authUid);
          if (latest?.permissions.includes("admin.manageRoles")) await refresh();
          setIdentifier("");
        } catch (e) {
          setError(e instanceof Error ? e.message : "Unable to update admin role.");
        } finally {
          setBusy(false);
        }
      },
    );
  };

  const assignRole = async () => {
    setBusy(true);
    setError("");
    try {
      // Confirm a resolved account, then submit its UID. Email ownership changes
      // after lookup must not silently redirect the confirmed grant to someone else.
      const { user } = await Auth.fetchWithIdToken("/api/admin/roles/resolve", { identifier: identifier.trim() });
      setBusy(false);
      await update(user.uid, role, user.email);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to look up this account.");
    } finally {
      setBusy(false);
    }
  };

  if (!allowed) return <p>Admin role management requires Admin access.</p>;
  return (
    <section className="mx-auto max-w-5xl px-2 py-6 text-left text-app-grey-0 sm:px-6">
      <header className="mb-7">
        <h1 className="m-0 text-2xl font-semibold tracking-tight">Admin roles</h1>
        <p className="mb-0 mt-2 text-sm text-app-grey-1">Manage who can access the admin panel and what they can do.</p>
        <AdminRolesHelp />
      </header>
      {error && (
        <p
          role="alert"
          className="mb-5 rounded-xl border border-solid border-app-danger-color p-3 text-sm text-app-danger-color"
        >
          {error}
        </p>
      )}
      <div className="mb-7 rounded-xl border border-solid border-app-divider bg-app-menu-color p-5">
        <h2 className="m-0 text-base font-semibold">Assign access</h2>
        <p
          id="admin-role-user-id-help"
          className="mb-0 mt-1 text-sm text-app-grey-1"
        >
          Enter the user’s email address or User ID, then choose their role.
        </p>
        <form
          className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            if (!busy && identifier.trim()) void assignRole();
          }}
        >
          <div className="min-w-0 flex-1">
            <label
              htmlFor="admin-role-user-id"
              className="mb-1.5 block text-xs font-medium text-app-grey-1"
            >
              Email address or User ID
            </label>
            <input
              id="admin-role-user-id"
              aria-describedby="admin-role-user-id-help"
              placeholder="name@example.com or User ID"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              disabled={busy}
              required
              className="box-border h-10 w-full rounded-lg border border-solid border-app-divider bg-app-document-background px-3 text-sm text-app-grey-0 outline-none focus:border-app-primary-cta disabled:opacity-50"
            />
          </div>
          <div className="sm:w-40">
            <label
              htmlFor="admin-role-choice"
              className="mb-1.5 block text-xs font-medium text-app-grey-1"
            >
              Role
            </label>
            <select
              id="admin-role-choice"
              value={role}
              onChange={(event) => setRole(event.target.value as AdminRole)}
              disabled={busy}
              className="box-border h-10 w-full rounded-lg border border-solid border-app-divider bg-app-document-background px-3 text-sm text-app-grey-0 outline-none focus:border-app-primary-cta disabled:opacity-50"
            >
              {ROLE_OPTIONS.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <SudoButton
            type="submit"
            variant="primary"
            disabled={busy || !identifier.trim()}
          >
            Assign role
          </SudoButton>
        </form>
      </div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2
          id="admin-role-assignments"
          className="m-0 text-base font-semibold"
        >
          People with access
        </h2>
        {!loading && !error && (
          <span className="text-sm text-app-grey-1">
            {assignments.length} {assignments.length === 1 ? "person" : "people"}
          </span>
        )}
      </div>
      <div className="overflow-x-auto rounded-xl border border-solid border-app-divider">
        <table
          aria-labelledby="admin-role-assignments"
          className="w-full min-w-[680px] border-collapse text-left text-sm"
        >
          <thead className="bg-app-menu-color text-xs text-app-grey-1">
            <tr>
              <th
                scope="col"
                className="px-4 py-3 font-medium"
              >
                User
              </th>
              <th
                scope="col"
                className="px-4 py-3 font-medium"
              >
                Role
              </th>
              <th
                scope="col"
                className="px-4 py-3 font-medium"
              >
                Last updated
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right font-medium"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedAssignments.map((assignment) => (
              <tr
                key={assignment.user_id}
                className="border-t border-solid border-app-divider hover:bg-app-list-element-hover"
              >
                <td className="px-4 py-4">
                  <a
                    href={`/admin/user/${encodeURIComponent(assignment.user_id)}`}
                    title={assignment.email || assignment.user_id}
                    className="block max-w-[260px] truncate text-sm font-medium text-app-primary-cta-high-contrast"
                  >
                    {assignment.email || "Email unavailable"}
                  </a>
                  <span
                    title={assignment.user_id}
                    className="mt-1 block max-w-[260px] truncate font-mono text-xs text-app-grey-1"
                  >
                    {assignment.user_id}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${assignment.role === "admin" ? "bg-app-primary-cta-translucent text-app-primary-cta-high-contrast" : "bg-app-list-element-hover text-app-grey-0"}`}
                  >
                    {assignment.role === "admin" ? "Admin" : "Support"}
                  </span>
                </td>
                <td className="whitespace-nowrap px-4 py-4 text-xs text-app-grey-1">
                  <time
                    dateTime={assignment.updated_at}
                    title={new Date(assignment.updated_at).toLocaleString()}
                  >
                    {new Date(assignment.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </time>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <ChangeRoleMenu
                      role={assignment.role}
                      disabled={busy}
                      onChange={(nextRole) => void update(assignment.user_id, nextRole, assignment.email)}
                    />
                    <SudoButton
                      size="small"
                      variant="error"
                      disabled={busy}
                      onClick={() => update(assignment.user_id, null, assignment.email)}
                    >
                      Remove
                    </SudoButton>
                  </div>
                </td>
              </tr>
            ))}
            {!assignments.length && (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-10 text-center text-sm text-app-grey-1"
                  role="status"
                >
                  {loading ? "Loading assignments…" : error ? "Assignments could not be loaded." : "No admin roles assigned yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mb-0 mt-3 text-xs text-app-grey-1">Only Admins can manage roles. At least one Admin must retain access.</p>
    </section>
  );
}
